import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, deleteDoc, updateDoc, doc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import db from '../firebase';

const VoteButtons = ({ article }) => {
    const { currentUser } = useAuth();
    const [userVote, setUserVote] = useState(null);
    const [voteCount, setVoteCount] = useState({
        upvotes: article.upvotes ?? 0,    // Use nullish coalescing
        downvotes: article.downvotes ?? 0
    });

    // Add useEffect to sync with article updates
    useEffect(() => {
        setVoteCount({
            upvotes: article.upvotes ?? 0,
            downvotes: article.downvotes ?? 0
        });
    }, [article.upvotes, article.downvotes]);

    // Add useEffect to fetch user's existing vote
    useEffect(() => {
        const fetchUserVote = async () => {
            if (!currentUser || !article) return;

            try {
                const votesRef = collection(db, 'votes');
                const q = query(
                    votesRef,
                    where('userId', '==', currentUser.uid),
                    where('articleId', '==', article.id)
                );
                const querySnapshot = await getDocs(q);
                const existingVote = querySnapshot.docs[0];

                if (existingVote) {
                    setUserVote(existingVote.data().voteType);
                }
            } catch (error) {
                console.error('Error fetching user vote:', error);
            }
        };

        fetchUserVote();
    }, [currentUser, article.id]);

    const handleVote = async (voteType) => {
        if (!currentUser) {
            alert('Please log in to vote');
            return;
        }

        const votesRef = collection(db, 'votes');
        const articleRef = doc(db, 'articles', article.id);

        try {
            // Query for existing vote
            const q = query(
                votesRef,
                where('userId', '==', currentUser.uid),
                where('articleId', '==', article.id)
            );
            const querySnapshot = await getDocs(q);
            const existingVoteDoc = querySnapshot.docs[0];

            let newUpvotes = voteCount.upvotes;
            let newDownvotes = voteCount.downvotes;

            // Handle vote removal (clicking same button)
            if (userVote === voteType) {
                if (existingVoteDoc) {
                    await deleteDoc(existingVoteDoc.ref);
                }
                
                if (voteType === 1) {
                    newUpvotes = Math.max(0, voteCount.upvotes - 1);
                } else {
                    newDownvotes = Math.max(0, voteCount.downvotes - 1);
                }

                await updateDoc(articleRef, {
                    upvotes: newUpvotes,
                    downvotes: newDownvotes,
                    vote: newUpvotes - newDownvotes
                });

                setUserVote(null);
                setVoteCount({ upvotes: newUpvotes, downvotes: newDownvotes });
                return;
            }

            // Handle vote change or new vote
            if (userVote) {
                // Remove old vote
                if (userVote === 1) {
                    newUpvotes = Math.max(0, voteCount.upvotes - 1);
                } else {
                    newDownvotes = Math.max(0, voteCount.downvotes - 1);
                }
            }

            // Add new vote
            if (voteType === 1) {
                newUpvotes++;
            } else {
                newDownvotes++;
            }

            // Update or create vote document
            if (existingVoteDoc) {
                await updateDoc(existingVoteDoc.ref, {
                    voteType,
                    timestamp: new Date()
                });
            } else {
                await addDoc(votesRef, {
                    userId: currentUser.uid,
                    articleId: article.id,
                    voteType,
                    timestamp: new Date()
                });
            }

            // Update article
            await updateDoc(articleRef, {
                upvotes: newUpvotes,
                downvotes: newDownvotes,
                vote: newUpvotes - newDownvotes
            });

            setUserVote(voteType);
            setVoteCount({ upvotes: newUpvotes, downvotes: newDownvotes });

        } catch (error) {
            console.error('Error handling vote:', error);
        }
    };

    return (
        <div className="vote-buttons">
            <button
                onClick={() => handleVote(1)}
                className={`upvote ${userVote === 1 ? 'active' : ''}`}
                disabled={!currentUser}
            >
                ▲ {voteCount.upvotes}
            </button>
            <span>{voteCount.upvotes - voteCount.downvotes}</span>
            <button
                onClick={() => handleVote(-1)}
                className={`downvote ${userVote === -1 ? 'active' : ''}`}
                disabled={!currentUser}
            >
                ▼ {voteCount.downvotes}
            </button>
        </div>
    );
};

export default VoteButtons;